import React, { useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Layouts
import AuthLayout from "./layouts/AuthLayout";
import DashboardLayout from "./layouts/DashboardLayout";

// Auth Pages
import Login from "./pages/auth/Login";
import OtpVerify from "./pages/auth/OtpVerify";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";

// Public Pages
import RegistrationPortal from "./pages/public/RegistrationPortal";

// Common Pages
import WelcomeScreen from "./pages/common/WelcomeScreen";
import ConsentForm from "./pages/common/ConsentForm";
import DepartmentView from "./pages/common/DepartmentView";
import StaffLeaves from "./pages/common/StaffLeaves";
import Dashboard from "./pages/dashboard/Dashboard";
import Profile from "./pages/profile/Profile";
import Notifications from "./pages/notifications/Notifications";
import AuditLogs from "./pages/common/AuditLogs";

// Student Pages
import StudentAssignments from "./pages/student/Assignments";
import StudentMaterials from "./pages/student/Materials";
import StudentAttendance from "./pages/student/Attendance";
import StudentLeaves from "./pages/student/Leaves";
import StudentTimetable from "./pages/student/Timetable";
import StudentExamResults from "./pages/student/ExamResults";
import StudentReports from "./pages/student/Reports";

// Teacher Pages
import TeacherStudents from "./pages/teacher/Students";
import TeacherAssignments from "./pages/teacher/TeacherAssignments";
import TeacherMaterials from "./pages/teacher/TeacherMaterials";
import TeacherLeaves from "./pages/teacher/TeacherLeaves";
import TeacherTimetable from "./pages/teacher/TeacherTimetable";
import TeacherExams from "./pages/teacher/TeacherExams";
import { EXAM_MODULE_PERMISSIONS } from "./utils/examPermissions";
import { HALL_TICKET_PERMISSIONS } from "./utils/hallTicketPermissions";
import { CHAT_MENU_PERMISSIONS } from "./utils/chatPermissions";
import { ALL_IMPORT_PERMISSION_SLUGS } from "./utils/importExportPermissions";
import { LECTURE_SWAP_VIEW_PERMISSION } from "./utils/lectureSwapPermissions";
import { EDIT_LECTURE_DEPARTMENT_VIEW, EDIT_LECTURE_VIEW } from "./utils/editLecturePermissions";
import TeacherReports from "./pages/teacher/TeacherReports";
import PromotionHistory from "./pages/teacher/PromotionHistory";
import PromotionHistoryDetail from "./pages/teacher/PromotionHistoryDetail";

// HOD Pages
import HODDepartment from "./pages/hod/Department";
import HODCourses from "./pages/hod/Courses";
import HODSubjects from "./pages/hod/Subjects";
import HODTeachers from "./pages/hod/Teachers";
import HODClasses from "./pages/hod/Classes";

// Admin Pages
import AdminUsers from "./pages/admin/Users";
import DeviceApprovals from "./pages/admin/DeviceApprovals";
import MobileAppVersions from "./pages/admin/MobileAppVersions";
import AdminDepartments from "./pages/admin/Departments";
import AdminRoles from "./pages/admin/Roles";
import AdminRBAC from "./pages/admin/RBAC";
import AdminRolesPermissions from "./pages/admin/RolesPermissions";
import AdminImport from "./pages/admin/Import";
import LeaveManagement from "./pages/admin/LeaveManagement";
import ApproveRegistration from "./pages/admin/ApproveRegistration";
import RegistrationSettings from "./pages/admin/RegistrationSettings";
import EventsManagement from "./pages/admin/EventsManagement";
import StudentPromotion from "./pages/admin/StudentPromotion";
import SpecialSessionsManagement from "./pages/admin/SpecialSessionsManagement";
import SpecialSessionDetails from "./pages/admin/SpecialSessionDetails";

// Guardian Faculty Member (GFM) Pages
import GfmSettings from "./pages/gfm/GfmSettings";
import GfmAssignments from "./pages/gfm/GfmAssignments";
import GfmDefaulterDashboard from "./pages/gfm/GfmDefaulterDashboard";
import FeesRouteSuspense from "./pages/fees/FeesRouteSuspense";
import {
  PaymentSetupPage,
  FeeHeadsManagementPage,
  FeeStructureManagementPage,
  StudentFeesManagementPage,
  FeeCollectionPage,
  FeeReportsPage,
  InstituteFeeSettingsPage,
  StudentFeeDashboardPage,
} from "./pages/fees/lazyFees";

// Documents Pages
import DocumentsPage from "./pages/admin/documents/DocumentsPage";

// Timetable Pages (Shared by Admin, Institute, HOD)
import { TimetableManagement, GenerationWizard, TimetableView, TimetableAnalytics, TimetableRegenerate, TimetableKanbanEditor, PDFStyleTimetableView } from "./pages/admin/timetable";

// Super Admin Pages
import SuperAdminInstitutes from "./pages/superadmin/Institutes";
import SuperAdminUsers from "./pages/superadmin/SuperAdminUsers";
import SuperAdminRBAC from "./pages/superadmin/RBAC";
import SuperAdminGlobalNotifications from "./pages/superadmin/GlobalNotifications";
import SuperAdminBroadcast from "./pages/superadmin/BroadcastManagement";
import SuperAdminImport from "./pages/superadmin/SuperAdminImport";
import SuperAdminAllUsers from "./pages/superadmin/AllUsersManagement";
import RolesPermissions from "./pages/superadmin/RolesPermissions";
import ConsentFormManagement from "./pages/superadmin/ConsentFormManagement";

// Analytics Pages
import AllStudentsAnalytics from "./pages/analytics/AllStudentsAnalytics";
import AllTeachersAnalytics from "./pages/analytics/AllTeachersAnalytics";
import AttendanceAnalytics from "./pages/analytics/AttendanceAnalytics";
import AssignmentsAnalytics from "./pages/analytics/AssignmentsAnalytics";
import DepartmentsAnalytics from "./pages/analytics/DepartmentsAnalytics";
import InstitutesAnalytics from "./pages/analytics/InstitutesAnalytics";
import AdvancedStudentReports from "./pages/analytics/AdvancedStudentReports";

// Chat Pages
import Chat from "./pages/chat/Chat";
import GroupChat from "./pages/chat/GroupChat";

// Enhanced Teacher Pages
import AttendanceWithQR from "./pages/teacher/AttendanceWithQR";
import ScheduleManualAttendance from "./pages/teacher/ScheduleManualAttendance";
import ManualAttendanceUpload from "./pages/teacher/ManualAttendanceUpload";
import TimetableScheduleChange from "./pages/teacher/TimetableScheduleChange";
import ExamManagement from "./pages/teacher/ExamManagement";
import HallTicketManagement from "./pages/teacher/HallTicketManagement";
import ClassNotifications from "./pages/teacher/ClassNotifications";
import LeaveApproval from "./pages/teacher/LeaveApproval";
import AssignmentApproval from "./pages/teacher/AssignmentApproval";
import LectureSwapManagement from "./pages/teacher/swap/LectureSwapManagement";
import DepartmentSchedule from "./pages/teacher/DepartmentSchedule";
import EditOverrideHistory from "./pages/teacher/EditOverrideHistory";

// Profile Pages
import ContactInfoRequests from "./pages/profile/ContactInfoRequests";
import ContactSupport from "./pages/common/ContactSupport";

// Organization Pages
import OrganizationDetails from "./pages/organization/OrganizationDetails";
import AcademicInfo from "./pages/organization/AcademicInfo";
import InstituteFaqManagement from "./pages/help/InstituteFaqManagement";

// Settings Page
import Settings from "./pages/settings/Settings";

// Protected Route Component
import ProtectedRoute from "./components/common/ProtectedRoute";
import RoleBasedRoute from "./components/common/RoleBasedRoute";
import PermissionBasedRoute from "./components/common/PermissionBasedRoute";

// Actions
import { checkAuth, setConsentGate } from "./store/slices/authSlice";
import { fetchMyPermissions } from "./store/slices/permissionsSlice";
import { registerConsentGateHandler } from "./utils/consentGate";
import TeacherRequests from "./pages/teacher/TeacherRequests";

/** First URL segment when it is a role-scoped area (matches routes using rolePath). Used before Redux user is rehydrated on full page refresh. */
const ROLE_PATH_PREFIXES = new Set(["admin", "hod", "teacher", "student", "superadmin"]);

const normalizeRoleSlug = (rawRole) => {
  if (!rawRole) return "";
  const s = String(rawRole).trim().toLowerCase().replace(/[_\s]+/g, "");
  if (s === "institute") return "admin";
  if (s === "superadmin" || s === "superadministrator") return "superadmin";
  return s;
};

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { isInInstituteContext } = useSelector((state) => state.instituteContext);

  const firstSegment =
    location.pathname.split("/").find((s) => s.length > 0)?.toLowerCase() ?? "";
  const pathRoleHint = ROLE_PATH_PREFIXES.has(firstSegment) ? firstSegment : null;

  // Normalize role slug for route path/guards (e.g., "Super Admin" -> "superadmin").
  const rawRole = user?.role || pathRoleHint || "hod";
  const role = normalizeRoleSlug(rawRole) || "hod";
  // Super Admin managing an institute uses /admin/* routes (must match Sidebar effectiveRolePath).
  const rolePath =
    role === "superadmin" && isInInstituteContext ? "/admin" : `/${role}`;

  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  useEffect(() => {
    registerConsentGateHandler((payload) => {
      dispatch(setConsentGate(payload));
      if (payload?.consentRequired && location.pathname !== "/consent") {
        navigate("/consent", { replace: true });
      }
    });
    return () => registerConsentGateHandler(null);
  }, [dispatch, navigate, location.pathname]);

  useEffect(() => {
    if (isAuthenticated && user) {
      dispatch(fetchMyPermissions());
    }
  }, [dispatch, isAuthenticated, user]);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        newestOnTop
        closeOnClick
        pauseOnHover
        theme="colored"
        style={{ zIndex: 20000 }}
      />
    <Routes>
      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-otp" element={<OtpVerify />} />
        {/* Reset password route - supports both token in URL (direct link) and token in state (from OTP) */}
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Public Registration Portal - no auth required */}
        <Route path="/registration" element={<RegistrationPortal />} />
      </Route>

      {/* Protected Routes */}
      <Route element={<ProtectedRoute isAuthenticated={isAuthenticated} />}>
        {/* Welcome & Consent */}
        <Route path="/welcome" element={<WelcomeScreen />} />
        <Route path="/consent" element={<ConsentForm />} />

        {/* Dashboard Layout Routes */}
        <Route element={<DashboardLayout />}>
          {/* Common Routes */}
          <Route
            element={<PermissionBasedRoute requiredPermission="dashboard.read" />}
          >
            <Route path="/dashboard" element={<Dashboard />} />
          </Route>
          <Route path="/profile" element={<Profile />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route
            element={<PermissionBasedRoute requiredPermission="organization.read" />}
          >
            <Route path="/organization" element={<OrganizationDetails />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute
                requiredAnyPermission={[
                  "faq.read",
                  "faq.create",
                  "organization.read",
                  "institute.read",
                  "user.read",
                  "department.read",
                ]}
              />
            }
          >
            <Route path="/help-faqs" element={<InstituteFaqManagement />} />
          </Route>
          <Route
            element={<PermissionBasedRoute requiredPermission="academic.read" />}
          >
            <Route path="/academic-info" element={<AcademicInfo />} />
          </Route>
          <Route path="/settings" element={<Settings />} />
          <Route
            element={
              <PermissionBasedRoute
                requiredAnyPermission={["department.info", "department.read"]}
              />
            }
          >
            <Route path="/department" element={<DepartmentView />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute
                requiredAnyPermission={["rbac.manage", "permission.manage", "role.manage", "audit.read"]}
              />
            }
          >
            <Route path={`${rolePath}/audit-logs`} element={<AuditLogs />} />
          </Route>

          {/* Analytics Routes - report.read + roles teacher/hod/admin/superadmin (students use /student/reports only) */}
          <Route
            element={
              <RoleBasedRoute
                allowedRoles={["teacher", "hod", "admin", "superadmin", "institute"]}
              />
            }
          >
            <Route path="/analytics/students" element={<AllStudentsAnalytics />} />
            <Route path="/analytics/teachers" element={<AllTeachersAnalytics />} />
            <Route path="/analytics/attendance" element={<AttendanceAnalytics />} />
            <Route path="/analytics/assignments" element={<AssignmentsAnalytics />} />
            <Route path="/analytics/departments" element={<DepartmentsAnalytics />} />
            <Route element={<PermissionBasedRoute requiredPermission="report.read" />}>
              <Route path="/analytics/reports" element={<AdvancedStudentReports />} />
              <Route path="/analytics/reports/department/:departmentId" element={<AdvancedStudentReports />} />
              <Route path="/analytics/reports/class/:classId" element={<AdvancedStudentReports />} />
              <Route path="/analytics/reports/student/:studentId" element={<AdvancedStudentReports />} />
            </Route>
          </Route>
          {/* Institutes Analytics - superadmin only */}
          <Route
            element={<RoleBasedRoute allowedRoles={["superadmin"]} />}
          >
            <Route path="/analytics/institutes" element={<InstitutesAnalytics />} />
          </Route>

          <Route element={<PermissionBasedRoute requiredAnyPermission={CHAT_MENU_PERMISSIONS} />}>
            <Route path="/chat" element={<Chat />} />
            <Route path="/chat/group/:groupId" element={<GroupChat />} />
          </Route>

          {/* Staff Leave Routes - permission-based for dynamic RBAC */}
          <Route
            element={
              <RoleBasedRoute permissionSlug="leave.apply" />
            }
          >
            <Route path="/apply-leave" element={<StaffLeaves />} />
          </Route>

          {/* Student Routes - permission-based for dynamic RBAC */}
          <Route element={<PermissionBasedRoute requiredPermission="assignment.read" />}>
            <Route path="/student/assignments" element={<StudentAssignments />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="material.read" />}>
            <Route path="/student/materials" element={<StudentMaterials />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="attendance.read" />}>
            <Route path="/student/attendance" element={<StudentAttendance />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="leave.apply" />}>
            <Route path="/student/leaves" element={<StudentLeaves />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="timetable.read" />}>
            <Route path="/student/timetable" element={<StudentTimetable />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="exam.result.view" />}>
            <Route path="/student/exam-results" element={<StudentExamResults />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="report.read" />}>
            <Route path="/student/reports" element={<StudentReports />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredAnyPermission={["fees.pay", "fees.read"]} />}>
            <Route
              path="/student/fees"
              element={
                <FeesRouteSuspense>
                  <StudentFeeDashboardPage />
                </FeesRouteSuspense>
              }
            />
          </Route>

          {/* Teacher/Staff Routes - use rolePath for dynamic roles, permission-based access */}
          <Route element={<PermissionBasedRoute requiredPermission="student.profile.read" />}>
            <Route path={`${rolePath}/students`} element={<TeacherStudents />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute
                requiredAnyPermission={["attendance.smart", "attendance.read", "attendance.start", "attendance.mark", "attendance.mark_manual"]}
              />
            }
          >
            <Route path={`${rolePath}/smart-attendance`} element={<AttendanceWithQR />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute
                requiredAnyPermission={["attendance.smart", "attendance.read", "attendance.start", "attendance.mark", "attendance.mark_manual"]}
              />
            }
          >
            <Route path={`${rolePath}/smart-attendance/manual`} element={<ScheduleManualAttendance />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute
                requiredAnyPermission={["attendance.manual_upload", "attendance.mark_manual", "attendance.start"]}
              />
            }
          >
            <Route path={`${rolePath}/attendance/manual-upload`} element={<ManualAttendanceUpload />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="assignment.read" />}>
            <Route path={`${rolePath}/assignments`} element={<TeacherAssignments />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="material.read" />}>
            <Route path={`${rolePath}/materials`} element={<TeacherMaterials />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute
                requiredAnyPermission={["leave.read", "leave.approve", "leave.reject"]}
              />
            }
          >
            <Route path={`${rolePath}/leaves`} element={<TeacherLeaves />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute
                requiredAnyPermission={["student.profile.update", "teacher.profile.update"]}
              />
            }
          >
            <Route path={`${rolePath}/requests`} element={<TeacherRequests />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute requiredAnyPermission={["leave.approve", "leave.reject"]} />
            }
          >
            <Route path={`${rolePath}/leave-approval`} element={<LeaveApproval />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="timetable.read" />}>
            <Route path={`${rolePath}/my-timetable`} element={<TeacherTimetable />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute requiredAnyPermission={EXAM_MODULE_PERMISSIONS} />
            }
          >
            <Route path={`${rolePath}/exams`} element={<TeacherExams />} />
            <Route path={`${rolePath}/exam-management`} element={<ExamManagement />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute
                requiredPermission={HALL_TICKET_PERMISSIONS.CONFIGURE}
              />
            }
          >
            <Route
              path={`${rolePath}/exam-management/:examId/hall-ticket`}
              element={<HallTicketManagement />}
            />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="report.read" />}>
            <Route path={`${rolePath}/reports`} element={<TeacherReports />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute
                requiredAnyPermission={["attendance.read", "attendance.start", "attendance.mark", "attendance.mark_manual"]}
              />
            }
          >
            <Route path={`${rolePath}/smart-attendance`} element={<AttendanceWithQR />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute
                requiredPermission="schedule.request_change"
                disallowedRoles={["institute"]}
              />
            }
          >
            <Route path={`${rolePath}/schedule-change`} element={<TimetableScheduleChange />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="notification.create_class" />}>
            <Route path={`${rolePath}/class-notifications`} element={<ClassNotifications />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute
                requiredAnyPermission={["assignment.approve", "assignment.reject"]}
              />
            }
          >
            <Route path={`${rolePath}/assignment-approval`} element={<AssignmentApproval />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission={LECTURE_SWAP_VIEW_PERMISSION} />}>
            <Route path={`${rolePath}/lecture-swap`} element={<LectureSwapManagement />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission={EDIT_LECTURE_DEPARTMENT_VIEW} />}>
            <Route path={`${rolePath}/edit-lecture/department-schedule`} element={<DepartmentSchedule />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission={EDIT_LECTURE_VIEW} />}>
            <Route path={`${rolePath}/edit-lecture/history`} element={<EditOverrideHistory />} />
          </Route>

          {/* Documents Route - permission-based for dynamic RBAC */}
          <Route element={<PermissionBasedRoute requiredPermission="documents.view" />}>
            <Route path={`${rolePath}/documents`} element={<DocumentsPage />} />
          </Route>

          {/* HOD/Department Routes - permission-based for dynamic RBAC */}
          <Route element={<PermissionBasedRoute requiredPermission="teacher.profile.read" />}>
            <Route path={`${rolePath}/teachers`} element={<HODTeachers />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredAnyPermission={["course.read", "course.import", "course.export"]} />}>
            <Route path={`${rolePath}/courses`} element={<HODCourses />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="class.read" />}>
            <Route path={`${rolePath}/classes`} element={<HODClasses />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="subject.read" />}>
            <Route path={`${rolePath}/subjects`} element={<HODSubjects />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredAnyPermission={["teacher.profile.read", "course.read", "department.read"]} />}>
            <Route path={`${rolePath}/department`} element={<HODDepartment />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="timetable.read" />}>
            <Route path={`${rolePath}/timetable`} element={<TimetableManagement />} />
            <Route path={`${rolePath}/timetable/view/:id`} element={<TimetableView />} />
            <Route path={`${rolePath}/timetable/pdf/:id`} element={<PDFStyleTimetableView />} />
            <Route path={`${rolePath}/timetable/edit/:id`} element={<TimetableKanbanEditor />} />
            <Route path={`${rolePath}/timetable/analytics/:id`} element={<TimetableAnalytics />} />
          </Route>
          {/* Generating/regenerating a timetable is a create operation. */}
          <Route element={<PermissionBasedRoute requiredPermission="timetable.create" />}>
            <Route path={`${rolePath}/timetable/generate`} element={<GenerationWizard />} />
            <Route path={`${rolePath}/timetable/regenerate/:id`} element={<TimetableRegenerate />} />
          </Route>

          {/* Admin Routes - permission-based for dynamic RBAC */}
          <Route element={<PermissionBasedRoute requiredPermission="user.read" />}>
            <Route path={`${rolePath}/users`} element={<AdminUsers />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute
                requiredAnyPermission={[
                  "device.student.read",
                  "device.student.approve",
                  "device.student.reject",
                  "device.staff.read",
                  "device.staff.approve",
                  "device.staff.reject",
                  "student.profile.update",
                  "teacher.profile.update",
                ]}
              />
            }
          >
            <Route path={`${rolePath}/requests/device-logins`} element={<DeviceApprovals />} />
            <Route path={`${rolePath}/device-approvals`} element={<DeviceApprovals />} />
            <Route path={`${rolePath}/mobile-app-versions`} element={<MobileAppVersions />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="department.read" />}>
            <Route path={`${rolePath}/departments`} element={<AdminDepartments />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredAnyPermission={ALL_IMPORT_PERMISSION_SLUGS} />}>
            <Route path={`${rolePath}/import`} element={<AdminImport />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute
                requiredAnyPermission={["leave.read", "leave.update", "leave.create"]}
              />
            }
          >
            <Route path={`${rolePath}/leave-management`} element={<LeaveManagement />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="registration.menu_view" />}>
            <Route path={`${rolePath}/approve-registration`} element={<ApproveRegistration />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredAnyPermission={["registration.prn.manage", "registration.manage"]} />}>
            <Route path={`${rolePath}/registration-settings`} element={<RegistrationSettings />} />
          </Route>

          {/* Guardian Faculty Member (GFM) Routes */}
          <Route element={<PermissionBasedRoute requiredPermission="gfm.manage" />}>
            <Route path={`${rolePath}/gfm/settings`} element={<GfmSettings />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredPermission="gfm.manage" />}>
            <Route path={`${rolePath}/gfm/assignments`} element={<GfmAssignments />} />
          </Route>
          <Route
            element={
              <PermissionBasedRoute
                requiredAnyPermission={["gfm.mentees.view", "gfm.view_all"]}
              />
            }
          >
            <Route
              path={`${rolePath}/gfm/defaulters`}
              element={<GfmDefaulterDashboard />}
            />
          </Route>
          <Route path={`${rolePath}/student-promotion`} element={<StudentPromotion />} />
          <Route
            element={
              <PermissionBasedRoute
                requiredAnyPermission={["student.promotion.logs", "student.profile.read", "student.profile.update"]}
              />
            }
          >
            <Route path={`${rolePath}/promotion-history`} element={<PromotionHistory />} />
            <Route path={`${rolePath}/promotion-history/:historyId`} element={<PromotionHistoryDetail />} />
          </Route>
          <Route path={`${rolePath}/events`} element={<EventsManagement />} />
          <Route
            element={
              <PermissionBasedRoute
                requiredAnyPermission={["special_session.read", "special_session.manage", "attendance.read", "attendance.start", "attendance.mark", "attendance.mark_manual"]}
              />
            }
          >
            <Route path={`${rolePath}/special-sessions`} element={<SpecialSessionsManagement />} />
            <Route path={`${rolePath}/special-sessions/:id`} element={<SpecialSessionDetails />} />
          </Route>

          <Route element={<PermissionBasedRoute requiredAnyPermission={["fees.payment_setup", "fees.manage"]} />}>
            <Route
              path={`${rolePath}/settings/payment-setup`}
              element={
                <FeesRouteSuspense>
                  <PaymentSetupPage />
                </FeesRouteSuspense>
              }
            />
          </Route>
          <Route element={<PermissionBasedRoute requiredAnyPermission={["fees.manage", "fees.read"]} />}>
            <Route
              path={`${rolePath}/fees/heads`}
              element={
                <FeesRouteSuspense>
                  <FeeHeadsManagementPage />
                </FeesRouteSuspense>
              }
            />
            <Route
              path={`${rolePath}/fees/structures`}
              element={
                <FeesRouteSuspense>
                  <FeeStructureManagementPage />
                </FeesRouteSuspense>
              }
            />
            <Route
              path={`${rolePath}/fees/student-fees`}
              element={
                <FeesRouteSuspense>
                  <StudentFeesManagementPage />
                </FeesRouteSuspense>
              }
            />
            <Route
              path={`${rolePath}/fees/collection`}
              element={
                <FeesRouteSuspense>
                  <FeeCollectionPage />
                </FeesRouteSuspense>
              }
            />
            <Route
              path={`${rolePath}/fees/reports`}
              element={
                <FeesRouteSuspense>
                  <FeeReportsPage />
                </FeesRouteSuspense>
              }
            />
            <Route
              path={`${rolePath}/fees/settings`}
              element={
                <FeesRouteSuspense>
                  <InstituteFeeSettingsPage />
                </FeesRouteSuspense>
              }
            />
          </Route>

          {/* Roles Management - permission-based */}
          <Route element={<PermissionBasedRoute requiredAnyPermission={["rbac.manage", "role.assign"]} />}>
            <Route path={`${rolePath}/roles`} element={<AdminRoles />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredAnyPermission={["rbac.manage", "role.assign"]} />}>
            <Route path={`${rolePath}/rbac`} element={<AdminRBAC />} />
          </Route>
          <Route element={<PermissionBasedRoute requiredAnyPermission={["rbac.manage", "role.assign"]} />}>
            <Route path={`${rolePath}/roles-permissions`} element={<AdminRolesPermissions />} />
          </Route>

          {/* Super Admin Routes - keep role check for platform-level, add permission checks */}
          <Route element={<RoleBasedRoute allowedRoles={["superadmin"]} />}>
            <Route
              element={<PermissionBasedRoute requiredPermission="institute.read" />}
            >
              <Route path={`${rolePath}/institutes`} element={<SuperAdminInstitutes />} />
            </Route>
            <Route
              element={<PermissionBasedRoute requiredPermission="admin.read" />}
            >
              <Route path={`${rolePath}/all-super-admins`} element={<SuperAdminUsers />} />
            </Route>
            <Route
              element={<PermissionBasedRoute requiredPermission="rbac.manage" />}
            >
              <Route path={`${rolePath}/rbac`} element={<SuperAdminRBAC />} />
            </Route>
            <Route
              path={`${rolePath}/roles-permissions`}
              element={<RolesPermissions />}
            />
            <Route path="/superadmin/global-notifications" element={<SuperAdminGlobalNotifications />} />
            <Route path="/superadmin/broadcast" element={<SuperAdminBroadcast />} />
            <Route path={`${rolePath}/import-all`} element={<SuperAdminImport />} />
            <Route
              path={`${rolePath}/all-users`}
              element={<SuperAdminAllUsers />}
            />
            <Route
              path={`${rolePath}/consent-forms`}
              element={<ConsentFormManagement />}
            />
          </Route>
          <Route path="/profile/contact-requests" element={<ContactInfoRequests />} />
          <Route element={<PermissionBasedRoute requiredPermission="support.email" />}>
            <Route path="/contact-support" element={<ContactSupport />} />
          </Route>
        </Route>
      </Route>

      {/* Default Redirect */}
      <Route
        path="/"
        element={
          <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default App;